import { Navigate, useLocation, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "@i18n";
import GridPageLayout from "@CubeComponents/html/GridPageLayout";
import pageBreak from "../src/assets/icons/svg/pageBreak.svg";
import { objectIdString } from "@Helpers/schemas/common.schema";
import type { ParamRule } from "./routeTypes";
import { canonicalSearch } from "./routeCanonical";

/** uuid v4 as it appears in a URL. */
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Whether a URL value satisfies the rule its route declared for it. */
function satisfies(value: string, rule: ParamRule): boolean {
  switch (rule) {
    case "objectId":
      return objectIdString.safeParse(value).success;
    case "uuid":
      return UUID_PATTERN.test(value);
    case "slug":
      // No format to check — a settings tab, a module name or a share token is opaque. Still
      // rejected when empty, which is the one malformed shape a slug can take in a URL.
      return value.length > 0;
  }
}

/**
 * Every param a route declares is checked against its own rule; `routeTypes.ts` makes that
 * declaration exhaustive, so no param reaches here unclassified.
 */
export function findMalformedParam(
  params: Readonly<Record<string, string | undefined>>,
  rules: Readonly<Record<string, ParamRule>>,
): string | null {
  for (const [name, rule] of Object.entries(rules)) {
    const value = params[name];
    // An absent optional param (`:settingType?`) is not malformed — it was simply not supplied.
    if (value === undefined) continue;
    if (!satisfies(value, rule)) return name;
  }
  return null;
}

/**
 * Same rules for query-string values, with one difference: a query param is optional by nature, so
 * ABSENT is always fine and only a present-but-malformed value blocks.
 */
export function findMalformedSearchParam(
  search: URLSearchParams,
  rules: Readonly<Record<string, ParamRule>>,
): string | null {
  for (const [name, rule] of Object.entries(rules)) {
    const value = search.get(name);
    if (value === null) continue;
    if (!satisfies(value, rule)) return name;
  }
  return null;
}

/**
 * Wraps every route (Router.tsx). A URL whose params break their rules gets the "Invalid URL" page; that check comes
 * first, so a malformed link is never handed a `?projectId` and the project read that follows it. A well-formed URL
 * missing the query its page needs (see `canonicalSearch`) is replaced, in place, by the same URL with it, so Back
 * never returns to the bare one. Otherwise the page renders.
 */
function RouteParamGuard({
  children,
  pattern,
  params: rules,
  searchParams: searchRules,
  portalList,
}: {
  children: React.ReactNode;
  /** The route path that matched, as routes.tsx declares it. */
  pattern: string;
  params?: Readonly<Record<string, ParamRule>>;
  searchParams?: Readonly<Record<string, ParamRule>>;
  portalList?: true | undefined;
}) {
  const params = useParams();
  const [search] = useSearchParams();
  const location = useLocation();
  const { t } = useTranslation();
  const malformed =
    (rules ? findMalformedParam(params, rules) : null) ??
    (searchRules ? findMalformedSearchParam(search, searchRules) : null);

  if (!malformed) {
    const next = canonicalSearch({ pattern, params, search, portalList });
    if (next === null) return children;
    // Whatever the previous navigation stored (`any` in the router's types); passed on unread.
    const state: unknown = location.state;
    return (
      <Navigate replace to={{ pathname: location.pathname, search: `?${next}`, hash: location.hash }} state={state} />
    );
  }

  return (
    <GridPageLayout showHeader={false}>
      <div className="h-full justify-center bg-white items-center flex gap-5 flex-col w-full">
        <div className="flex flex-col gap-2 items-center justify-center">
          <img src={pageBreak} alt="pageBreak" />
          <span className="font-semibol text-2xl">{t("Invalid URL")}</span>
          <span>{t("This page link is malformed or broken")}</span>
          <span>{t("Please check the link and try again")}</span>
        </div>
      </div>
    </GridPageLayout>
  );
}

export default RouteParamGuard;
